import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Send } from 'lucide-react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/toast'
import { FORM_CONTROL_FOCUS_CLASS } from '@/components/ui/form-control-styles'
import { primaryEmail } from '@/lib/clientContacts'
import { cn } from '@/lib/utils'
import { getApiErrorMessage } from '@/services/api'
import type { QuoteDetail, SendDocumentRequest } from '@/types'
import cardIcon from '@/assets/invoices/card.svg'
import handWritingIcon from '@/assets/invoices/hand-writing.svg'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const fieldClass = cn(
  'rounded-xl border-0 bg-[#f4f5f8] px-4 py-2.5 text-sm text-[#171a20] placeholder:text-[#8f95a6]',
  FORM_CONTROL_FOCUS_CLASS
)

const switchClass =
  'h-6 w-10 data-[state=checked]:bg-[#10b981] data-[state=unchecked]:bg-[#d5dae3]'

function buildQuoteSendBody(
  translate: (key: string, options?: Record<string, unknown>) => string,
  total: number,
  language: string,
  requestSignature: boolean,
  allowOnlinePayment: boolean
) {
  const amount = formatQuoteSummaryTotal(total, language)
  const infoParts: string[] = []

  infoParts.push(translate('detail.sendDialog.bodyValidity'))

  if (requestSignature) {
    infoParts.push(translate('detail.sendDialog.bodySignature'))
  }

  if (allowOnlinePayment) {
    infoParts.push(translate('detail.sendDialog.bodyOnlinePayment'))
  }

  infoParts.push(translate('detail.sendDialog.bodyQuestions'))

  return [
    translate('detail.sendDialog.bodyIntro', { amount }),
    infoParts.join(' '),
    [
      translate('detail.sendDialog.closingRegards'),
      translate('detail.sendDialog.closingTeam'),
    ].join('\n'),
  ].join('\n\n')
}

function FieldLabel({
  htmlFor,
  children,
  required,
}: {
  htmlFor: string
  children: string
  required?: boolean
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className='flex items-center gap-1 text-xs font-medium text-[#171a20]'
    >
      <span>{children}</span>
      {required && <span className='text-[#d63b33]'>*</span>}
    </Label>
  )
}

function SummaryRow({
  label,
  value,
  bordered = true,
  emphasized = false,
}: {
  label: string
  value: string
  bordered?: boolean
  emphasized?: boolean
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-4 py-2 text-sm leading-5',
        bordered && 'border-b border-[#e7e9ef]'
      )}
    >
      <span className='min-w-36 text-[#6c7285]'>{label}</span>
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-right font-medium',
          emphasized ? 'text-[#ff5a1f]' : 'text-[#171a20]'
        )}
      >
        {value}
      </span>
    </div>
  )
}

function PreferenceRow({
  id,
  label,
  description,
  checked,
  onCheckedChange,
  icon,
}: {
  id: string
  label: string
  description: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  icon: string
}) {
  return (
    <div className='flex flex-col gap-0'>
      <div className='flex items-center gap-3'>
        <div className='flex min-w-0 flex-1 items-center gap-1.5'>
          <img
            alt=''
            aria-hidden='true'
            className='size-4 shrink-0'
            src={icon}
          />
          <Label
            htmlFor={id}
            className='text-sm font-medium leading-[1.4] text-[#171a20]'
          >
            {label}
          </Label>
        </div>
        <Switch
          id={id}
          checked={checked}
          onCheckedChange={onCheckedChange}
          className={switchClass}
        />
      </div>
      <p className='text-xs leading-5 text-[#3d434e]'>{description}</p>
    </div>
  )
}

function formatQuoteSummaryTotal(value: number, language: string): string {
  return new Intl.NumberFormat(language.startsWith('fr') ? 'fr-FR' : 'en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
    .format(value)
    .replace(/\s/g, ' ')
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  quote: QuoteDetail
  total: number
  onSend: (body: SendDocumentRequest) => Promise<void>
  onSent?: () => Promise<unknown> | unknown
}

export function SendQuoteDialog({
  open,
  onOpenChange,
  quote,
  total,
  onSend,
  onSent,
}: Props) {
  const { t, i18n } = useTranslation('quotes')
  const defaultTo = quote.client?.email ?? primaryEmail(quote.client) ?? ''
  const defaultSubject = t('detail.sendSubject', {
    number: quote.number ?? '',
  }).trim()
  const summaryTotal = formatQuoteSummaryTotal(
    total,
    i18n.resolvedLanguage ?? 'fr'
  )
  const [to, setTo] = useState(defaultTo)
  const [subject, setSubject] = useState(defaultSubject)
  const [body, setBody] = useState('')
  const [requestSignature, setRequestSignature] = useState(
    !!quote.request_signature || !!quote.signature_request_id
  )
  const [allowOnlinePayment, setAllowOnlinePayment] = useState(
    !!quote.allow_online_payment
  )
  const [bodyDirty, setBodyDirty] = useState(false)
  const [errors, setErrors] = useState<{
    to?: string
    subject?: string
    body?: string
  }>({})
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (!open) return

    /* eslint-disable react-hooks/set-state-in-effect */
    setTo(defaultTo)
    setSubject(defaultSubject)
    const nextRequestSignature =
      !!quote.request_signature || !!quote.signature_request_id
    const nextAllowOnlinePayment = !!quote.allow_online_payment
    setRequestSignature(nextRequestSignature)
    setAllowOnlinePayment(nextAllowOnlinePayment)
    setBody(
      buildQuoteSendBody(
        t,
        total,
        i18n.resolvedLanguage ?? 'fr',
        nextRequestSignature,
        nextAllowOnlinePayment
      )
    )
    setBodyDirty(false)
    setErrors({})
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [
    i18n.resolvedLanguage,
    defaultSubject,
    defaultTo,
    open,
    quote,
    quote.allow_online_payment,
    quote.client?.name,
    quote.request_signature,
    quote.signature_request_id,
    quote.valid_until,
    total,
    t,
  ])

  const submit = async () => {
    const nextErrors: typeof errors = {}

    if (!to.trim() || !EMAIL_RE.test(to.trim())) {
      nextErrors.to = t('detail.sendDialog.toInvalid')
    }
    if (!subject.trim()) {
      nextErrors.subject = t('detail.sendDialog.subjectRequired')
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return
    }

    setSending(true)
    try {
      await onSend({
        recipient_email: to.trim(),
        subject: subject.trim(),
        message: body.trim(),
        request_signature: requestSignature,
        allow_online_payment: allowOnlinePayment,
      })
      await onSent?.()
      toast({ variant: 'success', title: t('detail.sendSuccess') })
      onOpenChange(false)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(error, t('detail.sendError')),
      })
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className='max-h-[calc(100vh-1.5rem)] max-w-[640px] gap-0 overflow-hidden border-[#e7e9ef] p-0 shadow-[0_1px_4px_rgba(38,45,66,0.05),0_8px_16px_-4px_rgba(38,45,66,0.1)] sm:rounded-2xl'
        showCloseButton={false}
      >
        <div className='max-h-[calc(100vh-1.5rem)] overflow-y-auto overflow-x-hidden px-6 py-6 sm:px-8 sm:py-8'>
          <div className='flex flex-col gap-6'>
            <DialogTitle className='pr-8 font-heading text-base font-semibold leading-[1.2] tracking-[-0.48px] text-[#171a20]'>
              {t('detail.sendTitle')}
            </DialogTitle>

            <div className='rounded-2xl bg-[#f4f5f8] px-4 py-2'>
              <SummaryRow
                label={t('detail.sendDialog.client')}
                value={quote.client?.name ?? '-'}
              />
              <SummaryRow
                label={t('detail.sendDialog.reference')}
                value={quote.number ?? quote.id}
              />
              <SummaryRow
                label={t('detail.sendDialog.total')}
                value={summaryTotal}
                bordered={false}
                emphasized
              />
            </div>

            <div className='space-y-1.5'>
              <FieldLabel htmlFor='quote-send-to' required>
                {t('detail.sendDialog.to')}
              </FieldLabel>
              <Input
                id='quote-send-to'
                type='email'
                value={to}
                onChange={event => {
                  setTo(event.target.value)
                  setErrors(current => ({ ...current, to: undefined }))
                }}
                className={cn(
                  fieldClass,
                  errors.to && 'ring-2 ring-[#d63b33]/20'
                )}
                placeholder={t('detail.sendDialog.toPlaceholder')}
              />
              {errors.to && (
                <p className='text-sm text-destructive'>{errors.to}</p>
              )}
            </div>

            <div className='space-y-1.5'>
              <FieldLabel htmlFor='quote-send-subject' required>
                {t('detail.sendDialog.subject')}
              </FieldLabel>
              <Input
                id='quote-send-subject'
                value={subject}
                maxLength={200}
                onChange={event => {
                  setSubject(event.target.value)
                  setErrors(current => ({ ...current, subject: undefined }))
                }}
                className={cn(
                  fieldClass,
                  errors.subject && 'ring-2 ring-[#d63b33]/20'
                )}
              />
              {errors.subject && (
                <p className='text-sm text-destructive'>{errors.subject}</p>
              )}
            </div>

            <div className='space-y-1.5'>
              <FieldLabel htmlFor='quote-send-body'>
                {t('detail.sendDialog.body')}
              </FieldLabel>
              <Textarea
                id='quote-send-body'
                value={body}
                maxLength={5000}
                rows={8}
                onChange={event => {
                  setBody(event.target.value)
                  setBodyDirty(true)
                  setErrors(current => ({ ...current, body: undefined }))
                }}
                className={cn(
                  'mb-1 min-h-[204px] rounded-xl border-0 bg-[#f4f5f8] px-4 py-3 text-sm leading-[1.4] text-[#171a20] placeholder:text-[#8f95a6]',
                  FORM_CONTROL_FOCUS_CLASS,
                  errors.body && 'ring-2 ring-[#d63b33]/20'
                )}
              />
              {errors.body && (
                <p className='text-sm text-destructive'>{errors.body}</p>
              )}
              <p className='text-sm leading-[1.4] text-[#3d434e]'>
                {t('detail.sendDialog.pdfAttachedNote')}
              </p>
            </div>

            <div className='space-y-4'>
              <PreferenceRow
                id='quote-request-signature'
                checked={requestSignature}
                onCheckedChange={checked => {
                  setRequestSignature(checked)
                  if (!bodyDirty) {
                    setBody(
                      buildQuoteSendBody(
                        t,
                        total,
                        i18n.resolvedLanguage ?? 'fr',
                        checked,
                        allowOnlinePayment
                      )
                    )
                  }
                }}
                label={t('detail.sendDialog.requestSignature')}
                description={t('detail.sendDialog.requestSignatureDescription')}
                icon={handWritingIcon}
              />

              <PreferenceRow
                id='quote-allow-online-payment'
                checked={allowOnlinePayment}
                onCheckedChange={checked => {
                  setAllowOnlinePayment(checked)
                  if (!bodyDirty) {
                    setBody(
                      buildQuoteSendBody(
                        t,
                        total,
                        i18n.resolvedLanguage ?? 'fr',
                        requestSignature,
                        checked
                      )
                    )
                  }
                }}
                label={t('detail.sendDialog.allowOnlinePayment')}
                description={t(
                  'detail.sendDialog.allowOnlinePaymentDescription'
                )}
                icon={cardIcon}
              />
            </div>

            <div className='flex flex-col-reverse gap-3 sm:flex-row sm:justify-end'>
              <Button
                type='button'
                variant='outline'
                className='h-10 rounded-xl border-[#e7e9ef] px-3 text-sm font-medium text-[#171a20] hover:bg-[#fbfbfc]'
                disabled={sending}
                onClick={() => onOpenChange(false)}
              >
                {t('detail.sendDialog.cancel')}
              </Button>
              <Button
                type='button'
                className='h-10 rounded-xl bg-[#ff5a1f] px-3 text-sm font-medium text-white hover:bg-[#e6511c]'
                disabled={sending}
                onClick={submit}
              >
                {sending ? (
                  <Loader2 className='mr-2 size-4 animate-spin' />
                ) : (
                  <Send className='mr-2 size-4' />
                )}
                {t('detail.sendDialog.send')}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default SendQuoteDialog
